import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Seo } from "../components/Seo";
import { SiteNav } from "../components/SiteNav";
import { SiteFooter } from "../components/SiteFooter";
import { WirelessOffersSchema } from "../components/StructuredData";
import { PhoneCheck, PlanQuiz } from "../components/WirelessTools";
import { useScanTracking } from "../lib/scanTracking";
import { Check, ArrowUpRight, QrCode, ArrowRightLeft, CreditCard, Signal, Smartphone, Wifi, Info } from "lucide-react";

export const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/364e14e3-15fd-48b2-b3bf-07611febddfe/images/a2adc1ee7635f1a154dc5d086b17dc104e81525fc9c8409bf9b7b7812ddf9ea5.jpeg";

const STEPS = [
    { icon: CreditCard, t: "Pick a plan & pay", d: "Everything is paid upfront at checkout — no surprise bills, no contract." },
    { icon: QrCode, t: "Get your eSIM instantly", d: "Your eSIM QR code is issued automatically the moment payment clears." },
    { icon: ArrowRightLeft, t: "Keep your number", d: "E-sign the transfer form online — we submit it to your old carrier for you." },
    { icon: Signal, t: "You're live", d: "Native calling, texting and 5G/LTE data straight from your phone's dialer." },
];

const usePlans = () => {
    const [state, setState] = React.useState({ plans: [], fees: { regulatory_monthly: 3.99, activation_one_time: 15, data_per_gb: 15 } });
    React.useEffect(() => {
        api.get("/mobile/plans").then(({ data }) => setState({ plans: data.plans || [], fees: data.fees || state.fees })).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return state;
};

export const useChoosePlan = () => {
    const { user, startLogin } = useAuth();
    const navigate = useNavigate();
    return (id) => {
        localStorage.setItem("flex_pending_plan", id);
        if (user) navigate(`/mobile?plan=${id}`);
        else startLogin();
    };
};

export const WirelessPlanCard = ({ p, onChoose, fees }) => (
    <div data-testid={`wireless-plan-${p.id}`}
        className={`relative rounded-2xl border p-6 flex flex-col transition-transform hover:-translate-y-1 ${p.popular ? "border-[#4C1D95] bg-[#4C1D95] text-white shadow-[0_30px_60px_-30px_rgba(76,29,149,0.7)]" : "border-[#DDD6FE] bg-white"}`}>
        {p.popular && <span className="absolute -top-3 left-6 bg-[#D97706] text-white text-[10px] font-mono uppercase px-2.5 py-1 rounded-full">Most popular</span>}
        {p.payg && <span className="absolute -top-3 left-6 bg-[#059669] text-white text-[10px] font-mono uppercase px-2.5 py-1 rounded-full">Pay for what you use</span>}
        <h3 className="font-display text-2xl">{p.name}</h3>
        <p className={`text-sm mt-1 ${p.popular ? "text-[#DDD6FE]" : "text-zinc-500"}`}>{p.best_for}</p>
        <div className="mt-5"><span className="font-display text-5xl font-light">${p.price}</span><span className={`text-sm ${p.popular ? "text-[#DDD6FE]" : "text-zinc-500"}`}>/mo</span></div>
        <p className={`text-sm font-medium mt-1 ${p.popular ? "text-[#FCD34D]" : "text-emerald-deep"}`} data-testid={`wireless-plan-data-${p.id}`}>{p.data}</p>
        {p.senior_price && <p className={`text-xs mt-1 ${p.popular ? "text-[#FCD34D]" : "text-[#B45309]"}`}>Seniors 62+: ${p.senior_price}/mo</p>}
        <ul className="mt-5 space-y-2 text-sm flex-1">
            {(p.features || []).filter((f) => !f.startsWith("Seniors")).map((f) => (
                <li key={f} className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.popular ? "text-[#FCD34D]" : "text-emerald-deep"}`} />{f}</li>
            ))}
        </ul>
        <p className={`text-[11px] mt-5 ${p.popular ? "text-[#DDD6FE]" : "text-zinc-500"}`}>+ ${fees.regulatory_monthly}/mo regulatory &amp; 911 fee · ${fees.activation_one_time} one-time activation</p>
        <Button onClick={() => onChoose(p.id)} data-testid={`wireless-choose-${p.id}`}
            className={`mt-4 rounded-full h-11 ${p.popular ? "bg-[#D97706] hover:bg-[#B45309] text-white" : "bg-[#4C1D95] hover:bg-[#2E1065] text-white"}`}>
            Choose {p.name} <ArrowUpRight className="w-4 h-4 ml-1" />
        </Button>
    </div>
);

export default function Wireless() {
    const { plans, fees } = usePlans();
    const choose = useChoosePlan();
    useScanTracking("/wireless");
    return (
        <div className="min-h-screen bg-cream text-zinc-900" data-testid="wireless-page">
            <Seo title="Wireless eSIM Plans — 1GB, 3GB, 5GB & Pay-As-You-Go · FlexLine Mobile"
                description="FlexLine Mobile wireless plans: Flex 1GB $19.99, Flex 3GB $29.99, Flex 5GB $39.99 or Pay-As-You-Go. Unlimited talk & text, 5G/LTE, eSIM activated automatically, keep your number."
                path="/wireless" />
            <WirelessOffersSchema plans={plans} />
            <SiteNav />

            <section className="relative overflow-hidden bg-[#2E1065] text-white">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2E1065] via-[#2E1065]/85 to-transparent" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[#FCD34D] mb-5">— Product 01 · Wireless service</p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[0.95] tracking-tight max-w-3xl">
                        A real cell phone plan.<br /><span className="font-serif-italic text-[#6EE7B7]">On eSIM, in minutes.</span>
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#E9D5FF] max-w-xl">
                        Your phone's own dialer, texts and 5G/LTE data — on the FlexLine network. Bring your number or get a new one.
                        No store, no SIM card, no contract.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#E9D5FF]">
                        {["Unlimited talk & text", "5G / LTE data", "Keep your number", "Activated automatically"].map((t) => (
                            <span key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#6EE7B7]" /> {t}</span>
                        ))}
                    </div>
                </div>
            </section>

            <section id="plans" className="border-b border-[#DDD6FE]" data-testid="wireless-plans">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
                    <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Pick your data. <span className="font-serif-italic text-[#4C1D95]">Talk &amp; text are unlimited.</span></h2>
                    <p className="text-zinc-600 mt-3 max-w-2xl">Every plan is paid upfront for the month. Need more data? Add a 1 GB pack any time from your dashboard.</p>
                    <div className="mt-8 grid lg:grid-cols-2 gap-5">
                        <PlanQuiz plans={plans} onChoose={choose} />
                        <PhoneCheck />
                    </div>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {plans.map((p) => <WirelessPlanCard key={p.id} p={p} fees={fees} onChoose={choose} />)}
                    </div>
                    <div className="mt-8 grid md:grid-cols-3 gap-4 text-sm" data-testid="wireless-fees">
                        <div className="bg-white border border-[#DDD6FE] rounded-sm p-4"><p className="font-medium">${fees.activation_one_time} activation (one-time)</p><p className="text-zinc-600">Covers your eSIM and number setup. Charged at checkout.</p></div>
                        <div className="bg-white border border-[#DDD6FE] rounded-sm p-4"><p className="font-medium">${fees.regulatory_monthly}/mo regulatory &amp; 911</p><p className="text-zinc-600">Standard carrier fee for E911 and federal programs.</p></div>
                        <div className="bg-white border border-[#DDD6FE] rounded-sm p-4"><p className="font-medium">Pay-As-You-Go data: ${fees.data_per_gb}/GB</p><p className="text-zinc-600">Only on the Pay-As-You-Go plan, billed for what you use (max 5 GB).</p></div>
                    </div>
                </div>
            </section>

            <section className="border-b border-[#DDD6FE] bg-white" data-testid="wireless-how">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">— Fully automated activation</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">From checkout to connected — no waiting on anyone.</h2>
                    <div className="mt-10 grid md:grid-cols-4 gap-4">
                        {STEPS.map(({ icon: Icon, t, d }, i) => (
                            <div key={t} className="border border-[#DDD6FE] rounded-2xl p-6 relative">
                                <span className="absolute top-5 right-5 font-mono text-xs text-[#B45309]">0{i + 1}</span>
                                <Icon className="w-6 h-6 text-[#4C1D95]" />
                                <p className="font-medium mt-4">{t}</p>
                                <p className="text-sm text-zinc-600 mt-1">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-[#DDD6FE] rounded-2xl p-6" data-testid="wireless-requirements">
                        <Smartphone className="w-5 h-5 text-emerald-deep" />
                        <p className="font-medium mt-3">What you need</p>
                        <ul className="text-sm text-zinc-600 mt-2 space-y-1.5">
                            <li>• An unlocked, eSIM-capable phone (iPhone XS or newer, Pixel 3 or newer, Galaxy S20 or newer)</li>
                            <li>• To keep your number: your old carrier account number &amp; transfer PIN</li>
                        </ul>
                    </div>
                    <div className="bg-white border border-[#DDD6FE] rounded-2xl p-6" data-testid="wireless-vs-second-number">
                        <Wifi className="w-5 h-5 text-[#B45309]" />
                        <p className="font-medium mt-3">Only need an extra number?</p>
                        <p className="text-sm text-zinc-600 mt-2 flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" />
                            Our <a href="/second-number" className="link-underline text-[#4C1D95] font-medium mx-1">second phone number</a> app adds a private texting &amp; calling line to the phone you already have — from $7.99/mo, no eSIM.</p>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
